import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../components/Avatar.js";
import { RatingLockup, Tally } from "../components/RatingLockup.js";
import { reliability, toDisplayRating } from "@rally/rating";
import { Segmented, Selector } from "../components/ui.js";
import { useAuth } from "../lib/auth.js";
import { fetchLeaderboard, type LeaderboardRow } from "../lib/leaderboard.js";
import { APP_ICON, APP_SHORT_NAME, SPORT_LABEL } from "../lib/sport.js";
import { ordinal, type Kind, type MemberCategory } from "../lib/types.js";

/**
 * The home screen. Not a dashboard, not your profile — open the app, see where you stand.
 * The leaderboard is the emotional core of the product.
 *
 * There is no sport switcher: this app is one sport. Doubles and singles remain separate
 * boards because they are separate ratings.
 *
 * Category is a filter over one board, not a second rating. Choosing Men or Women changes
 * who you are being compared against and nothing else. There is no Mixed board: mixed
 * matches are recorded and do not count towards a rating, so no number comes out of them.
 */
export function Leaderboard() {
  const { member } = useAuth();

  const [kind, setKind] = useState<Kind>("doubles");

  // Derived, not snapshotted. `useState(member?.category ?? "all")` captures the value at
  // first render and never looks again, so setting your category left the board stuck on
  // Everyone until a full reload. `chosen` is null until you actually pick something, and
  // until then the board follows your own category.
  //
  // Everyone remains the fallback for a member with no category: the board must never open
  // on a filter that hides you from it.
  const [chosen, setChosen] = useState<MemberCategory | "all" | null>(null);
  const category = chosen ?? member?.category ?? "all";
  const setCategory = setChosen;

  const { data, isPending, error } = useQuery({
    queryKey: ["leaderboard", kind, category],
    queryFn: () => fetchLeaderboard(kind, category === "all" ? undefined : category),
  });

  // Whether this community plays the other discipline at all, which is a different question
  // from whether this board is empty. Asked so an empty board can say which it is.
  const { data: otherKind } = useQuery({
    queryKey: ["leaderboard", kind === "doubles" ? "singles" : "doubles", "all"],
    queryFn: () => fetchLeaderboard(kind === "doubles" ? "singles" : "doubles"),
  });

  const settled = data?.filter((row) => !row.provisional) ?? [];
  const provisional = data?.filter((row) => row.provisional) ?? [];

  const myIndex = member ? settled.findIndex((row) => row.memberId === member.id) : -1;

  return (
    <main className="mx-auto max-w-2xl px-5 pb-16 pt-4">
      <header className="mb-5">
        <div className="mb-3 flex items-center gap-2.5">
          <img src={APP_ICON} alt="" className="size-8 object-contain" />
          <div className="font-display text-[22px] leading-none" style={{ fontWeight: 900 }}>
            {APP_SHORT_NAME}
          </div>
        </div>
        <div className="font-mono mb-1.5 flex justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
          <span>{SPORT_LABEL} · season table</span>
          {data && data.length > 0 ? (
            <span>
              {settled.length} rated
              {provisional.length > 0 ? ` · ${provisional.length} settling` : ""}
            </span>
          ) : null}
        </div>
        <h1
          className="font-display uppercase"
          style={{ fontSize: "clamp(56px, 20vw, 78px)", fontWeight: 900, lineHeight: 0.82 }}
        >
          The Board
        </h1>
      </header>

      <div className="mb-4 flex gap-2.5">
        <Segmented
          label="Discipline"
          className="flex-1"
          value={kind}
          onChange={setKind}
          options={[
            { value: "doubles", label: "Doubles" },
            { value: "singles", label: "Singles" },
          ]}
        />
        <Selector
          label="Who you are compared with"
          value={category}
          onChange={(v) => setCategory(v as MemberCategory | "all")}
          options={[
            { value: "all", label: "Everyone" },
            { value: "men", label: "Men" },
            { value: "women", label: "Women" },
          ]}
        />
      </div>

      {isPending ? <p className="text-ink-muted">Loading the board…</p> : null}
      {error ? (
        <p style={{ color: "var(--color-down)" }}>
          Couldn&rsquo;t load the board. Check your connection and try again.
        </p>
      ) : null}

      {data && data.length === 0 ? (
        <EmptyBoard
          kind={kind}
          category={category}
          otherKindHasPlayers={(otherKind?.length ?? 0) > 0}
        />
      ) : null}

      {myIndex >= 0 ? (
        <YourLine
          row={settled[myIndex]!}
          rank={myIndex + 1}
          of={settled.length}
          above={myIndex > 0 ? settled[myIndex - 1] : undefined}
        />
      ) : null}

      {settled.length > 0 ? (
        <div className="mt-7">
          <div className="font-mono flex items-center pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">
            <span className="w-9">Rk</span>
            <span className="flex-1">Player</span>
            <span className="w-[58px]">Rel</span>
            <span className="w-[78px] text-right">Rating</span>
          </div>
          <div className="tramline" aria-hidden />
        </div>
      ) : null}

      <ol>
        {settled.map((row, index) => (
          <Row key={row.memberId} row={row} rank={index + 1} you={index === myIndex} />
        ))}
      </ol>

      {/* Violet marks "not settled yet" rather than "lesser". These players stay visible
          on their own community's board — requirements §5.8 keeps them out of the ranking,
          not out of sight. */}
      {provisional.length > 0 ? (
        <>
          <div className="mb-1 mt-6 flex items-center gap-2.5">
            <div
              className="flex-1 border-t-[1.5px] border-dashed"
              style={{ borderColor: "var(--color-provisional)" }}
            />
            <span
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "var(--color-provisional)" }}
            >
              Provisional · still settling
            </span>
            <div
              className="flex-1 border-t-[1.5px] border-dashed"
              style={{ borderColor: "var(--color-provisional)" }}
            />
          </div>
          <ul>
            {provisional.map((row) => (
              <Row key={row.memberId} row={row} rank={null} you={row.memberId === member?.id} />
            ))}
          </ul>
        </>
      ) : null}
    </main>
  );
}

/**
 * Where you stand, before the list of where everyone stands. The one panel on the board,
 * because it is the answer to the question the app is opened to ask.
 */
function YourLine({
  row,
  rank,
  of,
  above,
}: {
  row: LeaderboardRow;
  rank: number;
  of: number;
  above: LeaderboardRow | undefined;
}) {
  const gap = above ? toDisplayRating(above.r) - toDisplayRating(row.r) : null;

  return (
    <section
      aria-label="Your line"
      className="border-2"
      style={{ borderColor: "var(--color-line)" }}
    >
      <div
        className="font-mono flex h-[30px] items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
      >
        <span>Your line</span>
        <span>
          Rank {rank} of {of}
        </span>
      </div>
      <div className="px-3.5 pb-3.5 pt-4">
        <RatingLockup
          r={row.r}
          rd={row.rd}
          delta={row.delta}
          matchesPlayed={row.matchesPlayed}
          size="hero"
        />
        {gap !== null ? (
          <p className="font-mono mt-1 text-[11px] text-ink-muted">
            {gap.toFixed(3).replace(/^0/, "")} behind {ordinal(rank - 1)}
          </p>
        ) : (
          <p className="font-mono mt-1 text-[11px] text-ink-muted">Top of the board</p>
        )}
      </div>
    </section>
  );
}

/**
 * An empty board has three different causes and they need three different sentences.
 * "No rated matches yet" is wrong and slightly insulting when the truth is that this
 * community plays doubles and nobody has ever played a singles match.
 */
function EmptyBoard({
  kind,
  category,
  otherKindHasPlayers,
}: {
  kind: Kind;
  category: MemberCategory | "all";
  otherKindHasPlayers: boolean;
}) {
  if (category !== "all") {
    return (
      <p className="text-ink-muted">
        Nobody in this category has a {kind} rating yet. Ratings appear once a match is confirmed —
        and a member with no category set is only ever on the Everyone board.
      </p>
    );
  }

  if (otherKindHasPlayers) {
    return (
      <p className="text-ink-muted">
        No {kind} matches yet. This community plays {kind === "doubles" ? "singles" : "doubles"},
        and nothing here changes until someone logs a {kind} match.
      </p>
    );
  }

  return (
    <p className="text-ink-muted">No rated matches yet. Log one and the board starts filling.</p>
  );
}

/**
 * A place on the board, and the way into the player standing in it.
 *
 * The whole row is the target rather than the name alone: this is read on a phone, and a
 * 58px row you can hit anywhere beats a 16px word you have to aim at. Nothing about it
 * announces itself as a link — the board is a list of standings first, and a row of blue
 * underlines would turn the emotional core of the product into a directory.
 */
function Row({ row, rank, you }: { row: LeaderboardRow; rank: number | null; you: boolean }) {
  const confidence = reliability(row.rd);

  return (
    <li
      className="border-b"
      style={{
        borderColor: "var(--color-line-muted)",
        // Your own row reads as the shaded line on a printed sheet, bled to the edges.
        background: you ? "var(--color-surface)" : undefined,
        margin: you ? "0 -20px" : undefined,
        padding: you ? "0 20px" : undefined,
      }}
    >
      <Link
        to={`/player/${row.memberId}`}
        className="flex items-center"
        style={{ minHeight: rank ? 58 : 62 }}
        aria-label={`${row.displayName}, ${rank ? `rank ${rank}` : "provisional"}`}
      >
        <span
          className="font-display w-9 shrink-0 text-[26px]"
          style={{ color: rank ? undefined : "var(--color-ink-muted)" }}
        >
          {rank ?? "–"}
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-2.5">
          <Avatar name={row.displayName} url={row.avatarUrl} size={32} />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <span className="truncate text-[15px] font-semibold">{row.displayName}</span>
              {you ? (
                <span
                  className="font-mono border-[1.5px] px-1 text-[9px] font-semibold tracking-[0.12em]"
                  style={{ borderColor: "var(--color-line)" }}
                >
                  YOU
                </span>
              ) : null}
            </span>
            {row.provisional ? (
              <span className="font-mono text-[10px]" style={{ color: "var(--color-provisional)" }}>
                {row.matchesPlayed} {row.matchesPlayed === 1 ? "match" : "matches"} so far
              </span>
            ) : null}
          </span>
        </span>
        <span className="w-[58px] shrink-0">
          <Tally confidence={confidence} unsettled={row.provisional} compact />
        </span>
        <span className="w-[78px] shrink-0">
          <RatingLockup
            r={row.r}
            rd={row.rd}
            delta={row.delta}
            size="row"
            provisional={row.provisional}
          />
        </span>
      </Link>
    </li>
  );
}
